/**
 * Date range picker guards.
 *
 * Extracted from SafeDateRangePicker (SUPPORT-617) so the "accidental
 * collapse" detection logic can be unit-tested without mounting the picker.
 */

/**
 * Decide whether a range-change emission should be rejected as an "accidental
 * collapse" — the pattern where the user clicks a date OUTSIDE the existing
 * range and VueDatePicker silently resets, then a second click on that same
 * date commits a one-day range, wiping out the boundary the user never meant
 * to touch.
 *
 * Returns `true` when the picker should restore the previous range instead of
 * emitting the overwrite; `false` when the new range is a legitimate edit.
 *
 * Allowed (returns false):
 * - Previous range was single-day already — nothing meaningful to lose.
 * - New range is multi-day — user clearly picked two distinct boundaries.
 * - New collapsed date falls within the previous range — user is narrowing,
 *   not replacing, e.g. picking the existing start or end as a single day.
 *
 * Rejected (returns true):
 * - Previous range was multi-day AND new range collapses to one day AND that
 *   one day is outside the previous range.
 *
 * @param {Date|null} prevStart  Previous start date (already parsed).
 * @param {Date|null} prevEnd  Previous end date (already parsed).
 * @param {Date|null} newStart  Incoming start date from the picker.
 * @param {Date|null} newEnd  Incoming end date from the picker.
 * @returns {boolean}
 */
export function shouldRejectCollapsedRange(
    prevStart,
    prevEnd,
    newStart,
    newEnd
) {
    if (!newStart || !newEnd) {
        return false;
    }

    if (!prevStart || !prevEnd) {
        return false;
    }

    const isCollapsedNew = newStart.getTime() === newEnd.getTime();
    const wasMultiDayPrev = prevStart.getTime() !== prevEnd.getTime();

    if (!isCollapsedNew || !wasMultiDayPrev) {
        return false;
    }

    const newTime = newStart.getTime();
    const wasInPrevRange =
        newTime >= prevStart.getTime() && newTime <= prevEnd.getTime();

    return !wasInPrevRange;
}

/**
 * Resolve the range to commit when the picker was opened from a specific
 * boundary input ("anchored" selection, SUPPORT-617).
 *
 * The two-input UI (Start / End) promises that editing one boundary leaves
 * the other untouched — the behaviour the pre-MY1-2411 per-input flatpickr
 * instances provided. When the menu is anchored, a single calendar click
 * commits the clicked date into the anchored boundary and keeps the other
 * boundary fixed. A click that would cross the fixed boundary (End < Start
 * from the End input, or Start > End from the Start input) is rejected so
 * the opposite boundary is never silently overwritten — the picker also
 * tightens min/max while anchored, so in practice the calendar disables
 * those cells before the user can click them.
 *
 * Returns `[start, end]` to commit, or `null` when anchored resolution does
 * not apply (no anchor, the opposite boundary is missing, or the click
 * would cross the fixed boundary) and the picker should fall back to its
 * normal two-click range selection.
 *
 * @param {'start'|'end'|null} anchor  Which input opened the picker.
 * @param {Date|null} prevStart  Previous start date (already parsed).
 * @param {Date|null} prevEnd  Previous end date (already parsed).
 * @param {Date|null} clicked  The calendar date the user clicked.
 * @returns {[Date, Date]|null}
 */
export function resolveAnchoredRange(anchor, prevStart, prevEnd, clicked) {
    if (!clicked || (anchor !== 'start' && anchor !== 'end')) {
        return null;
    }

    const fixed = anchor === 'start' ? prevEnd : prevStart;

    if (!fixed) {
        return null;
    }

    const range = anchor === 'start' ? [clicked, fixed] : [fixed, clicked];

    if (range[0].getTime() > range[1].getTime()) {
        return null;
    }

    return range;
}

/**
 * Decide what the first calendar click of a range selection should commit.
 *
 * `autoRangeDays` takes precedence over boundary anchoring: consumers that
 * auto-fill a fixed-length range (the hotel alert form passes
 * `auto-range-days="1"`, check-in + one night) expect every pick to restart
 * the range from the clicked date, regardless of which input opened the
 * picker. Anchoring there would hold the old opposite boundary fixed and
 * swap on cross — overwriting dates the user never touched, the exact
 * failure mode this component was fixed for (SUPPORT-617 re-review).
 *
 * Returns `{ range: [start, end], anchored: boolean }` to commit, or `null`
 * when neither applies and the picker should fall back to its normal
 * two-click range selection. `anchored` tells the caller whether the commit
 * finishes the interaction (anchored commits close the menu).
 *
 * @param {number|null} autoRangeDays  Fixed range length, in days.
 * @param {'start'|'end'|null} anchor  Which input opened the picker.
 * @param {Date|null} prevStart  Previous start date (already parsed).
 * @param {Date|null} prevEnd  Previous end date (already parsed).
 * @param {Date|null} clicked  The calendar date the user clicked.
 * @returns {{range: [Date, Date], anchored: boolean}|null}
 */
export function resolveRangeStartCommit(
    autoRangeDays,
    anchor,
    prevStart,
    prevEnd,
    clicked
) {
    if (!clicked) {
        return null;
    }

    if (autoRangeDays !== null && autoRangeDays > 0) {
        const end = new Date(clicked);
        end.setDate(end.getDate() + autoRangeDays);

        return { range: [new Date(clicked), end], anchored: false };
    }

    const anchoredRange = resolveAnchoredRange(
        anchor,
        prevStart,
        prevEnd,
        clicked
    );

    return anchoredRange ? { range: anchoredRange, anchored: true } : null;
}
